import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { formatDate } from '../utils/dateHelpers';
import pb from '../lib/pocketbaseClient';

export default function ClientPayments() {
  const [payments, setPayments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterProject, setFilterProject] = useState('All');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [formData, setFormData] = useState({
    project: '',
    amount: '',
    paymentDate: '',
    method: 'bank_transfer',
    note: '',
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const isMounted = useRef(true);

  const paymentMethods = ['cash', 'bank_transfer', 'cheque', 'upi', 'online'];

  // Auto-select project from query param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const projectId = params.get('projectId');
    if (projectId) {
      setFormData(prev => ({ ...prev, project: projectId }));
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    fetchData();
    return () => { isMounted.current = false; };
  }, []);

  const fetchData = async () => {
    try {
      const [paymentsRes, projectsRes] = await Promise.all([
        pb.collection('client_payments').getFullList({ sort: '-paymentDate', expand: 'project' }),
        pb.collection('accounts_projects').getFullList({ sort: 'name' }),
      ]);
      if (isMounted.current) {
        setPayments(paymentsRes);
        setProjects(projectsRes);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      const data = {
        project: formData.project,
        amount: parseFloat(formData.amount) || 0,
        paymentDate: formData.paymentDate,
        method: formData.method,
        note: formData.note || null,
      };

      if (editingPayment) {
        await pb.collection('client_payments').update(editingPayment.id, data);
      } else {
        await pb.collection('client_payments').create(data);
      }
      handleCloseModal();
      fetchData();
    } catch (err) {
      let msg = err.message || 'Failed to save payment';
      if (err.data?.data) {
        const fields = Object.entries(err.data.data).map(([k, v]) => `${k}: ${v.message}`).join('; ');
        msg += ` (${fields})`;
      }
      setFormError(msg);
      setFormLoading(false);
    }
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setFormData({
      project: payment.project || '',
      amount: payment.amount || '',
      paymentDate: payment.paymentDate || '',
      method: payment.method || 'bank_transfer',
      note: payment.note || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this client payment record?')) return;
    try {
      await pb.collection('client_payments').delete(id);
      fetchData();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPayment(null);
    setFormData({
      project: '',
      amount: '',
      paymentDate: '',
      method: 'bank_transfer',
      note: '',
    });
    setFormError('');
    setFormLoading(false);
  };

  const getMethodBadge = (method) => {
    const colors = {
      cash: 'bg-emerald-100 text-emerald-700',
      bank_transfer: 'bg-blue-100 text-blue-700',
      cheque: 'bg-amber-100 text-amber-700',
      upi: 'bg-purple-100 text-purple-700',
      online: 'bg-indigo-100 text-indigo-700',
    };
    return <Badge className={colors[method] || 'bg-slate-100 text-slate-700'}>{method.replace('_', ' ').toUpperCase()}</Badge>;
  };

  const filteredPayments = payments.filter(p => {
    const matchProject = filterProject === 'All' || p.project === filterProject;
    const matchSearch = p.note?.toLowerCase().includes(search.toLowerCase()) ||
                        p.expand?.project?.name?.toLowerCase().includes(search.toLowerCase());
    const matchDate = (!dateFrom || p.paymentDate >= dateFrom) && (!dateTo || p.paymentDate <= dateTo);
    return matchProject && matchSearch && matchDate;
  });

  const totalReceived = filteredPayments.reduce((sum, p) => sum + (p.amount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-500 font-medium">Loading client payments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">📈 Client Payment Received</h1>
          <p className="text-sm text-slate-500">Record and track payments received from clients</p>
        </div>
        <Button className="bg-amber-500 hover:bg-amber-600" onClick={() => { setEditingPayment(null); setShowModal(true); }}>
          + New Payment
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase font-semibold">Total Received</p><p className="text-2xl font-bold text-emerald-600">₹{totalReceived.toLocaleString()}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase font-semibold">Payment Count</p><p className="text-2xl font-bold text-slate-800">{filteredPayments.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase font-semibold">Avg. Payment</p><p className="text-2xl font-bold text-slate-800">{filteredPayments.length > 0 ? `₹${(totalReceived / filteredPayments.length).toFixed(0)}` : '₹0'}</p></CardContent></Card>
      </div>

      <div className="flex flex-wrap items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-slate-100">
        <div className="flex-1 min-w-[200px]">
          <Input placeholder="🔍 Search project or note..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full" />
        </div>
        <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400">
          <option value="All">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <div className="flex items-center gap-2">
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" />
          <span className="text-slate-400">to</span>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" />
        </div>
        <Button variant="outline" onClick={() => { setSearch(''); setFilterProject('All'); setDateFrom(''); setDateTo(''); }}>Clear Filters</Button>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Project</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Method</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider text-right">Amount</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Note</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredPayments.length === 0 ? (
                <tr><td colSpan="6" className="px-6 py-8 text-center text-slate-500">No client payments found.</td></tr>
              ) : (
                filteredPayments.map(p => (
                  <tr key={p.id} className="hover:bg-slate-50/50 transition">
                    <td className="px-6 py-3">{formatDate(p.paymentDate)}</td>
                    <td className="px-6 py-3 font-medium">{p.expand?.project?.name || 'N/A'}</td>
                    <td className="px-6 py-3">{getMethodBadge(p.method)}</td>
                    <td className="px-6 py-3 text-right font-mono font-bold text-emerald-600">₹{p.amount?.toLocaleString()}</td>
                    <td className="px-6 py-3">{p.note || '-'}</td>
                    <td className="px-6 py-3 text-center space-x-2">
                      <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700 hover:bg-amber-50" onClick={() => handleEdit(p)}>
                        Edit
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(p.id)}>
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-slate-800 mb-4">
                {editingPayment ? '✏️ Edit Client Payment' : '➕ New Client Payment'}
              </h2>
              {formError && (
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm mb-4 border border-red-200 whitespace-pre-wrap">
                  {formError}
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Project *</label>
                  <select
                    name="project"
                    value={formData.project}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                    required
                  >
                    <option value="">Select Project</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Amount (₹) *</label>
                    <Input name="amount" type="number" value={formData.amount} onChange={handleChange} required step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Payment Date *</label>
                    <Input name="paymentDate" type="date" value={formData.paymentDate} onChange={handleChange} required />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Payment Method *</label>
                  <select
                    name="method"
                    value={formData.method}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                    required
                  >
                    {paymentMethods.map(m => <option key={m} value={m}>{m.replace('_', ' ').toUpperCase()}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Note</label>
                  <Input name="note" value={formData.note} onChange={handleChange} placeholder="Any additional information" />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                  <Button type="button" variant="outline" onClick={handleCloseModal}>Cancel</Button>
                  <Button type="submit" className="bg-amber-500 hover:bg-amber-600" disabled={formLoading}>
                    {formLoading ? 'Saving...' : (editingPayment ? 'Update' : 'Create')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

